import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getArabicDate } from '../../services/utils/date';
import teacherService from '../../services/api/teacher';
import adminService from '../../services/api/admin';
import { 
  Save, 
  Upload, 
  Plus, 
  Trash2, 
  ArrowRight, 
  Image as ImageIcon, 
  Info,
  Layout,
  Type,
  FileText,
  Eye,
  ChevronRight,
  AlertCircle,
  CheckCircle2,
  Calendar,
  Layers,
  EyeOff,
  Globe,
  Star,
  XCircle
} from 'lucide-react';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useToast } from '../../components/common/Toast';

// Dynamic description function for course settings
const getCourseSettingDescription = (setting, value) => {
  const descriptions = {
    isPublicListed: {
      true: 'ظاهر في القوائم العامة للطلاب',
      false: 'مخفي عن القوائم العامة'
    },
    isFeatured: {
      true: 'معروض في قسم الكورسات المميزة',
      false: 'كورس عادي'
    },
    isActive: {
      true: 'متاح للطلاب حالياً',
      false: 'غير متاح مؤقتاً'
    }
  };
  
  return descriptions[setting]?.[value] || '';
};

// Enhanced setting titles with icons
const getCourseSettingTitle = (setting, value) => {
  const titles = {
    isPublicListed: {
      true: 'كورس عام',
      false: 'كورس خاص'
    },
    isFeatured: {
      true: 'كورس مميز',
      false: 'كورس عادي'
    },
    isActive: {
      true: 'كورس نشط',
      false: 'كورس معطل'
    }
  };
  
  return titles[setting]?.[value] || '';
};

const CreateCourse = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  
  // التحقق من وضع الإدارة
  const isAdminMode = useMemo(() => searchParams.get('mode') === 'admin', [searchParams]);

  const [formData, setFormData] = useState({
    teacherSubjectId: '',
    title: '',
    description: '',
    isPublicListed: true,
    order: 0,
    price: 0,
    discountPrice: '',
    discountEndDate: '',
    durationHours: '',
    durationMinutes: '',
    defaultDurationDays: 30,
    isFeatured: false,
    isActive: true,
    isArchived: false
  });

  const [thumbnail, setThumbnail] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Learning Outcomes and Requirements
  const [learningOutcomes, setLearningOutcomes] = useState(['']);
  const [requirements, setRequirements] = useState(['']);

  // جلب المواد الدراسية - تختلف حسب الوضع (معلم أو إدارة)
  const { data: subjects, isLoading: subjectsLoading } = useQuery(
    ['subjects', isAdminMode],
    () => isAdminMode ? adminService.getSubjects() : teacherService.getSubjects(),
    {
      retry: 1
    }
  );

  // التحقق من صحة البيانات (المنطق الأصلي كاملاً)
  const validateForm = () => {
    const newErrors = {};

    if (!formData.teacherSubjectId) {
      newErrors.teacherSubjectId = 'يرجى اختيار المادة الدراسية المستهدفة';
    }

    if (!formData.title.trim()) {
      newErrors.title = 'عنوان الكورس مطلوب للتعريف بالمحتوى';
    } else if (formData.title.length < 2 || formData.title.length > 255) {
      newErrors.title = 'يجب أن يكون العنوان بين 2 و 255 حرفاً';
    }

    if (formData.description && formData.description.length > 1000) {
      newErrors.description = 'وصف الكورس طويل جداً (الحد الأقصى 1000 حرف)';
    }

    if (formData.price && parseFloat(formData.price) < 0) {
      newErrors.price = 'السعر يجب أن يكون 0 أو أكبر';
    }
    
    if (formData.discountPrice && parseFloat(formData.discountPrice) < 0) {
      newErrors.discountPrice = 'سعر الخصم يجب أن يكون 0 أو أكبر';
    }
    
    if (formData.discountPrice && formData.price && parseFloat(formData.discountPrice) >= parseFloat(formData.price)) {
      newErrors.discountPrice = 'سعر الخصم يجب أن يكون أقل من السعر الأصلي';
    }
    
    if (formData.durationHours && (parseInt(formData.durationHours) < 0 || parseInt(formData.durationHours) > 999)) {
      newErrors.durationHours = 'عدد الساعات يجب أن يكون بين 0 و 999';
    }
    
    if (formData.durationMinutes && (parseInt(formData.durationMinutes) < 0 || parseInt(formData.durationMinutes) > 59)) {
      newErrors.durationMinutes = 'عدد الدقائق يجب أن يكون بين 0 و 59';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // إضافة/إزالة نتيجة تعلم
  const addLearningOutcome = () => {
    setLearningOutcomes([...learningOutcomes, '']);
  };
  
  const removeLearningOutcome = (index) => {
    setLearningOutcomes(learningOutcomes.filter((_, i) => i !== index));
  };
  
  const updateLearningOutcome = (index, value) => {
    const updated = [...learningOutcomes];
    updated[index] = value;
    setLearningOutcomes(updated);
  };
  
  // إضافة/إزالة متطلب
  const addRequirement = () => {
    setRequirements([...requirements, '']);
  };
  
  const removeRequirement = (index) => {
    setRequirements(requirements.filter((_, i) => i !== index));
  };
  
  const updateRequirement = (index, value) => {
    const updated = [...requirements];
    updated[index] = value;
    setRequirements(updated);
  };


  // تنفيذ عملية الإنشاء
  const createCourseMutation = useMutation(
    (courseData) => isAdminMode ? adminService.createContent('courses', courseData) : teacherService.createCourse(courseData),
    {
      onSuccess: (response) => {
        toast.success('تم إنشاء الكورس بنجاح! يمكنك الآن إضافة الدروس.');
        queryClient.invalidateQueries(isAdminMode ? ['admin-content', 'courses'] : 'teacher-courses');
        navigate(isAdminMode ? '/admin/content' : '/teacher/courses');
      },
      onError: (error) => {
        if (error.response?.data?.errors) {
          setErrors(error.response.data.errors);
          toast.error('يرجى تصحيح الأخطاء في النموذج');
        } else {
          const errMsg = error.response?.data?.error || 'فشل في إنشاء الكورس، حاول مرة أخرى.';
          setErrors({ submit: errMsg });
          toast.error(errMsg);
        }
      },
      onSettled: () => {
        setIsSubmitting(false);
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('يرجى التحقق من كافة الحقول المطلوبة');
      return;
    }

    setIsSubmitting(true);
    
    // إنشاء FormData لدعم رفع الملفات (النمط الاحترافي الموحد)
    const courseFormData = new FormData();
    courseFormData.append('teacherSubjectId', String(formData.teacherSubjectId));
    courseFormData.append('title', formData.title);
    courseFormData.append('description', formData.description);
    
    // booleans - explicit string conversion
    courseFormData.append('isPublicListed', String(formData.isPublicListed));
    courseFormData.append('isFeatured', String(formData.isFeatured));
    courseFormData.append('isActive', String(formData.isActive));
    courseFormData.append('isArchived', String(formData.isArchived));
    
    // numbers - price is required, others are optional
    courseFormData.append('price', String(formData.price));
    
    if (formData.order != null)
      courseFormData.append('order', String(formData.order));
    
    if (formData.discountPrice != null && formData.discountPrice !== '')
      courseFormData.append('discountPrice', String(formData.discountPrice));
    
    if (formData.durationHours != null && formData.durationHours !== '')
      courseFormData.append('durationHours', String(formData.durationHours));
    
    if (formData.durationMinutes != null && formData.durationMinutes !== '')
      courseFormData.append('durationMinutes', String(formData.durationMinutes));
    
    if (formData.defaultDurationDays != null)
      courseFormData.append('defaultDurationDays', String(formData.defaultDurationDays));
    
    // date - only if exists
    if (formData.discountEndDate) {
      courseFormData.append('discountEndDate', formData.discountEndDate);
    }
    
    // إضافة نتائج التعلم
    learningOutcomes.filter(outcome => outcome.trim()).forEach((outcome, index) => {
      courseFormData.append(`learningOutcomes[${index}]`, outcome);
    });
    
    // إضافة المتطلبات
    requirements.filter(requirement => requirement.trim()).forEach((requirement, index) => {
      courseFormData.append(`requirements[${index}]`, requirement);
    });
    
    if (thumbnail) {
      courseFormData.append('thumbnail', thumbnail);
    }

    createCourseMutation.mutate(courseFormData);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    
    if (file) {
      if (!file.type.startsWith('image/')) {
        setErrors(prev => ({ ...prev, thumbnail: 'عذراً، يجب أن يكون الملف المختار صورة فقط.' }));
        return;
      }
      
if (file.size > 50 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, thumbnail: 'حجم الصورة كبير جداً، الحد الأقصى هو 50 ميجابايت.' }));
        return;
      }
      
      setThumbnail(file);
      setThumbnailPreview(URL.createObjectURL(file));
      
      if (errors.thumbnail) {
        setErrors(prev => ({ ...prev, thumbnail: '' }));
      }
    }
  };

  const handleRemoveImage = () => {
    setThumbnail(null);
    setThumbnailPreview('');
    const fileInput = document.getElementById('thumbnail');
    if (fileInput) fileInput.value = '';
  };

  if (isSubmitting) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-bold animate-pulse uppercase tracking-widest text-xs">جاري حفظ بيانات الكورس...</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto py-10 px-4 sm:px-6 lg:px-8 bg-gray-50/30 min-h-screen">
      
      {/* Header & Breadcrumbs */}
      <div className="mb-10 flex flex-col md:flex-row md:items-center justify-between gap-6 animate-in fade-in slide-in-from-top duration-500">
        <div className="space-y-1">
            <button
                onClick={() => navigate(isAdminMode ? '/admin/content' : '/teacher/courses')}
                className="group flex items-center text-gray-400 hover:text-primary-600 font-bold transition-all mb-2 text-sm"
            >
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                العودة {isAdminMode ? 'لإدارة المحتوى' : 'لقائمة الكورسات'}
            </button>
            <h1 className="text-3xl font-black text-gray-900 tracking-tight flex items-center gap-3">
                <Plus className="bg-primary-600 text-white p-1.5 rounded-xl" size={32} />
                تجهيز كورس جديد
            </h1>
        </div>
        <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-2xl border border-gray-100 shadow-sm text-xs font-bold text-gray-500 uppercase">
            <Calendar size={14} className="text-primary-500" />
            تاريخ اليوم: {getArabicDate(new Date())}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Left Column: Main Info */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 lg:p-10">
            <h2 className="text-xl font-black text-gray-900 mb-8 flex items-center gap-3">
                <Layout size={20} className="text-primary-600" />
                البيانات الأساسية
            </h2>

            <div className="space-y-6">
              {/* Teacher Subject Select */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-black text-gray-700 mr-2 uppercase tracking-tighter">
                    <CheckCircle2 size={16} className="text-primary-500" /> المادة والصف الدراسي *
                </label>
                <div className="relative">
                    <select
                    id="teacherSubjectId"
                    name="teacherSubjectId"
                    value={formData.teacherSubjectId}
                    onChange={handleInputChange}
                    disabled={subjectsLoading}
                    className={`w-full px-5 py-4 bg-gray-50 border rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-700 appearance-none cursor-pointer ${
                        errors.teacherSubjectId ? 'border-red-300 bg-red-50/30' : 'border-gray-100'
                    }`}
                    >
                    <option value="">{subjectsLoading ? 'جاري جلب موادك...' : 'اختر المادة الدراسية المحددة لهذا الكورس'}</option>
                    {subjects?.map(subject => (
                        <option key={subject.teacher_subject_id || subject.id} value={subject.teacher_subject_id || subject.id}>
                        {isAdminMode ? subject.name : `مادة ${subject.subject_name} - ${subject.grade_name}`}
                        </option>
                    ))}
                    </select>
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                        <ChevronRight className="rotate-90" size={20} />
                    </div>
                </div>
                {errors.teacherSubjectId && (
                  <p className="flex items-center gap-1.5 mt-1 mr-2 text-xs font-bold text-red-500"><AlertCircle size={12}/> {errors.teacherSubjectId}</p>
                )}
              </div>

              {/* Title Input */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-black text-gray-700 mr-2 uppercase tracking-tighter">
                    <Type size={16} className="text-primary-500" /> عنوان الكورس التعليمي *
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="مثال: مراجعة ليلة الامتحان في اللغة العربية"
                  className={`w-full px-5 py-4 bg-gray-50 border rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-900 placeholder:text-gray-300 ${
                    errors.title ? 'border-red-300 bg-red-50/30' : 'border-gray-100'
                  }`}
                />
                {errors.title && (
                  <p className="flex items-center gap-1.5 mt-1 mr-2 text-xs font-bold text-red-500"><AlertCircle size={12}/> {errors.title}</p>
                )}
              </div>

              {/* Description Input */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-black text-gray-700 mr-2 uppercase tracking-tighter">
                    <FileText size={16} className="text-primary-500" /> وصف تفصيلي (اختياري)
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={6}
                  placeholder="ماذا سيتعلم الطالب في هذا الكورس؟"
                  className={`w-full px-5 py-4 bg-gray-50 border rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-medium text-gray-700 resize-none leading-relaxed ${
                    errors.description ? 'border-red-300 bg-red-50/30' : 'border-gray-100'
                  }`}
                />
              </div>
              <div className="flex justify-between items-center px-2 mt-1">
                <span className={`text-[10px] font-black uppercase tracking-widest ${formData.description.length > 900 ? 'text-orange-500' : 'text-gray-400'}`}>
                  {formData.description.length} / 1000 حرف
                </span>
                {errors.description && (
                  <p className="flex items-center gap-1.5 text-xs font-bold text-red-500"><AlertCircle size={12}/> {errors.description}</p>
                )}
              </div>

              {/* Default Duration Days Input */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-black text-gray-700 mr-2 uppercase tracking-tighter">
                    <Calendar size={16} className="text-primary-500" /> مدة الاشتراك الافتراضية (بالأيام)
                </label>
                <div className="relative">
                  <select
                    id="defaultDurationDays"
                    name="defaultDurationDays"
                    value={formData.defaultDurationDays}
                    onChange={handleInputChange}
                    className="w-full px-5 py-4 pr-16 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-900 appearance-none cursor-pointer"
                  >
                    <option value="">اختر مدة الاشتراك</option>
                    <option value={30}>30 يوم - اشتراك شهري</option>
                    <option value={120}>120 يوم - اشتراك ترم دراسي</option>
                    <option value={300}>300 يوم - اشتراك عام دراسي</option>
                  </select>
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                    <ChevronRight className="rotate-90" size={20} />
                  </div>
                </div>
                <p className="text-[10px] font-bold text-gray-400 mt-1 mr-2">
                  المدة الافتراضية لاشتراك الطالب في الكورس
                </p>
              </div>

              {/* Price Input */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-black text-gray-700 mr-2 uppercase tracking-tighter">
                    <CheckCircle2 size={16} className="text-primary-500" /> سعر الكورس (اختياري)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    id="price"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    className={`w-full px-5 py-4 pr-16 bg-gray-50 border rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-900 placeholder:text-gray-300 ${
                      errors.price ? 'border-red-300 bg-red-50/30' : 'border-gray-100'
                    }`}
                  />
                  <div className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 font-bold">
                    جنية
                  </div>
                </div>
                {errors.price && (
                  <p className="flex items-center gap-1.5 mt-1 mr-2 text-xs font-bold text-red-500"><AlertCircle size={12}/> {errors.price}</p>
                )}
                <p className="text-[10px] font-bold text-gray-400 mt-1 mr-2">
                  اتركه فارغاً إذا كان الكورس مجانياً
                </p>
              </div>

              {/* Discount Price Input */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-black text-gray-700 mr-2 uppercase tracking-tighter">
                    <CheckCircle2 size={16} className="text-primary-500" /> سعر بعد الخصم (اختياري)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    id="discountPrice"
                    name="discountPrice"
                    value={formData.discountPrice}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    className={`w-full px-5 py-4 pr-16 bg-gray-50 border rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-900 placeholder:text-gray-300 ${
                      errors.discountPrice ? 'border-red-300 bg-red-50/30' : 'border-gray-100'
                    }`}
                  />
                  <div className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 font-bold">
                    جنية
                  </div>
                </div>
                {errors.discountPrice && (
                  <p className="flex items-center gap-1.5 mt-1 mr-2 text-xs font-bold text-red-500"><AlertCircle size={12}/> {errors.discountPrice}</p>
                )}
              </div>

              {/* Discount End Date Input */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-black text-gray-700 mr-2 uppercase tracking-tighter">
                    <Calendar size={16} className="text-primary-500" /> تاريخ انتهاء الخصم (اختياري)
                </label>
                <input
                  type="date"
                  id="discountEndDate"
                  name="discountEndDate"
                  value={formData.discountEndDate}
                  onChange={handleInputChange}
                  className={`w-full px-5 py-4 bg-gray-50 border rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-900 placeholder:text-gray-300 ${
                    errors.discountEndDate ? 'border-red-300 bg-red-50/30' : 'border-gray-100'
                  }`}
                />
                {errors.discountEndDate && (
                  <p className="flex items-center gap-1.5 mt-1 mr-2 text-xs font-bold text-red-500"><AlertCircle size={12}/> {errors.discountEndDate}</p>
                )}
              </div>

              {/* Duration Inputs */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-black text-gray-700 mr-2 uppercase tracking-tighter">
                    <CheckCircle2 size={16} className="text-primary-500" /> مدة (ساعات)
                  </label>
                  <input
                    type="number"
                    id="durationHours"
                    name="durationHours"
                    value={formData.durationHours}
                    onChange={handleInputChange}
                    placeholder="0"
                    min="0"
                    max="999"
                    className={`w-full px-5 py-4 bg-gray-50 border rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-900 placeholder:text-gray-300 ${
                      errors.durationHours ? 'border-red-300 bg-red-50/30' : 'border-gray-100'
                    }`}
                  />
                  {errors.durationHours && (
                    <p className="flex items-center gap-1.5 mt-1 mr-2 text-xs font-bold text-red-500"><AlertCircle size={12}/> {errors.durationHours}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-black text-gray-700 mr-2 uppercase tracking-tighter">
                    <CheckCircle2 size={16} className="text-primary-500" /> مدة (دقائق)
                  </label>
                  <input
                    type="number"
                    id="durationMinutes"
                    name="durationMinutes"
                    value={formData.durationMinutes}
                    onChange={handleInputChange}
                    placeholder="0"
                    min="0"
                    max="59"
                    className={`w-full px-5 py-4 bg-gray-50 border rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-900 placeholder:text-gray-300 ${
                      errors.durationMinutes ? 'border-red-300 bg-red-50/30' : 'border-gray-100'
                    }`}
                  />
                  {errors.durationMinutes && (
                    <p className="flex items-center gap-1.5 mt-1 mr-2 text-xs font-bold text-red-500"><AlertCircle size={12}/> {errors.durationMinutes}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Media & Actions */}
        <div className="space-y-6">
          
          {/* Thumbnail Section */}
          <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8">
            <h2 className="text-xl font-black text-gray-900 mb-6 flex items-center gap-3">
                <ImageIcon size={20} className="text-primary-600" />
                غلاف الكورس
            </h2>

            <div className="space-y-6">
                {thumbnailPreview ? (
                    <div className="relative group rounded-3xl overflow-hidden shadow-xl ring-4 ring-gray-50">
                        <img 
                            src={thumbnailPreview} 
                            alt="Preview"
                            className="w-full aspect-video object-cover transition-transform duration-500 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <button
                                type="button"
                                onClick={handleRemoveImage}
                                className="bg-red-600 text-white p-3 rounded-2xl hover:bg-red-700 transition-all shadow-lg transform hover:scale-110 active:scale-95"
                            >
                                <Trash2 size={24} />
                            </button>
                        </div>
                    </div>
                ) : (
                    <label 
                        htmlFor="thumbnail"
                        className="w-full aspect-video border-4 border-dashed border-gray-100 rounded-[2rem] flex flex-col items-center justify-center group hover:border-primary-200 hover:bg-primary-50/30 transition-all cursor-pointer"
                    >
                        <div className="bg-primary-50 text-primary-600 p-4 rounded-2xl mb-3 group-hover:scale-110 transition-transform">
                            <Upload size={32} />
                        </div>
                        <span className="text-sm font-black text-gray-900">اضغط لرفع صورة</span>
                        <span className="text-[10px] font-bold text-gray-400 mt-1">JPG, PNG (Max 50MB)</span>
                        <input
                            type="file"
                            id="thumbnail"
                            accept="image/*"
                            onChange={handleImageChange}
                            className="hidden"
                        />
                    </label>
                )}
                {errors.thumbnail && (
                    <p className="flex items-center justify-center gap-1.5 text-xs font-bold text-red-500 text-center animate-pulse"><AlertCircle size={12}/> {errors.thumbnail}</p>
                )}
            </div>
          </div>

          {/* Learning Outcomes Section */}
          <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8">
            <h2 className="text-xl font-black text-gray-900 mb-6 flex items-center gap-3">
                <CheckCircle2 size={20} className="text-primary-600" />
                أهداف التعلم
            </h2>
            <div className="space-y-4">
              {learningOutcomes.map((outcome, index) => (
                <div key={index} className="flex gap-3">
                  <input
                    type="text"
                    value={outcome}
                    onChange={(e) => updateLearningOutcome(index, e.target.value)}
                    placeholder="ماذا سيتعلم الطالب بعد هذا الكورس؟"
                    className="flex-1 px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-medium text-gray-700 placeholder:text-gray-400"
                  />
                  {learningOutcomes.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeLearningOutcome(index)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-xl transition-all"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addLearningOutcome}
                className="w-full py-3 bg-primary-50 border-2 border-dashed border-primary-200 text-primary-600 rounded-xl hover:bg-primary-100 transition-all flex items-center justify-center gap-2 font-medium"
              >
                <Plus size={16} />
                إضافة هدف تعلم جديد
              </button>
            </div>
          </div>

          {/* Requirements Section */}
          <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8">
            <h2 className="text-xl font-black text-gray-900 mb-6 flex items-center gap-3">
                <AlertCircle size={20} className="text-primary-600" />
                المتطلبات
            </h2>
            <div className="space-y-4">
              {requirements.map((requirement, index) => (
                <div key={index} className="flex gap-3">
                  <input
                    type="text"
                    value={requirement}
                    onChange={(e) => updateRequirement(index, e.target.value)}
                    placeholder="ماذا يجب أن يعرفه الطالب قبل هذا الكورس؟"
                    className="flex-1 px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-medium text-gray-700 placeholder:text-gray-400"
                  />
                  {requirements.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeRequirement(index)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-xl transition-all"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addRequirement}
                className="w-full py-3 bg-primary-50 border-2 border-dashed border-primary-200 text-primary-600 rounded-xl hover:bg-primary-100 transition-all flex items-center justify-center gap-2 font-medium"
              >
                <Plus size={16} />
                إضافة متطلب جديد
              </button>
            </div>
          </div>

          {/* Settings Section */}
          <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8">
            <h2 className="text-lg font-black text-gray-900 mb-6 flex items-center gap-3 uppercase tracking-tighter">
                <Eye size={18} className="text-primary-600" /> إعدادات الظهور
            </h2>
            
            <div className="space-y-4">
              <div className="bg-gray-50/50 p-5 rounded-2xl border border-gray-100 flex items-center justify-between group">
                <div className="flex flex-col">
                  <span className="text-sm font-black text-gray-700 flex items-center gap-2">
                    {formData.isPublicListed ? <Globe size={14} className="text-blue-600" /> : <EyeOff size={14} className="text-gray-400" />}
                    {getCourseSettingTitle('isPublicListed', formData.isPublicListed)}
                  </span>
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">
                    {getCourseSettingDescription('isPublicListed', formData.isPublicListed)}
                  </span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    id="isPublicListed"
                    name="isPublicListed"
                    checked={formData.isPublicListed}
                    onChange={handleInputChange}
                    className="sr-only peer"
                  />
                  <div className="w-12 h-7 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[-1.25rem] peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:right-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="bg-gray-50/50 p-5 rounded-2xl border border-gray-100 flex items-center justify-between group">
                <div className="flex flex-col">
                  <span className="text-sm font-black text-gray-700 flex items-center gap-2">
                    {formData.isFeatured ? <Star size={14} className="text-yellow-500" /> : <Star size={14} className="text-gray-400" />}
                    {getCourseSettingTitle('isFeatured', formData.isFeatured)}
                  </span>
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">
                    {getCourseSettingDescription('isFeatured', formData.isFeatured)}
                  </span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    id="isFeatured"
                    name="isFeatured"
                    checked={formData.isFeatured}
                    onChange={handleInputChange}
                    className="sr-only peer"
                  />
                  <div className="w-12 h-7 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[-1.25rem] peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:right-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-yellow-500"></div>
                </label>
              </div>

              <div className="bg-gray-50/50 p-5 rounded-2xl border border-gray-100 flex items-center justify-between group">
                <div className="flex flex-col">
                  <span className="text-sm font-black text-gray-700 flex items-center gap-2">
                    {formData.isActive ? <CheckCircle2 size={14} className="text-green-600" /> : <XCircle size={14} className="text-red-600" />}
                    {getCourseSettingTitle('isActive', formData.isActive)}
                  </span>
                  <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">
                    {getCourseSettingDescription('isActive', formData.isActive)}
                  </span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    id="isActive"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                    className="sr-only peer"
                  />
                  <div className="w-12 h-7 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-[-1.25rem] peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:right-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-green-600"></div>
                </label>
              </div>
            </div>

            <div className="space-y-2">
                <label className="text-[11px] font-black text-gray-400 uppercase tracking-widest mr-2">ترتيب العرض (أولوية)</label>
                <div className="relative">
                    <input
                        type="number"
                        id="order"
                        name="order"
                        value={formData.order}
                        onChange={handleInputChange}
                        min="0"
                        className="w-full px-5 py-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white transition-all font-bold text-gray-900"
                    />
                    <Layers className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={16} />
                </div>
            </div>

            <div className="mt-8 space-y-4">
                <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-primary-600 text-white py-4 rounded-2xl font-black shadow-xl shadow-primary-100 hover:bg-primary-700 transition-all flex items-center justify-center gap-3 disabled:opacity-50 transform active:scale-95"
                >
                    <Save size={20} />
                    {isSubmitting ? 'جاري المعالجة...' : 'إنشاء ونشر الكورس'}
                </button>
                <button
                    type="button"
                    onClick={() => navigate(isAdminMode ? '/admin/content' : '/teacher/courses')}
                    className="w-full bg-white border border-gray-100 text-gray-400 py-4 rounded-2xl font-bold hover:bg-gray-50 transition-all text-sm"
                >
                    إلغاء العملية
                </button>
            </div>
            
            {errors.submit && (
                <div className="mt-4 p-4 bg-red-50 border border-red-100 rounded-2xl">
                    <p className="text-[10px] font-black text-red-600 flex items-center gap-2 uppercase"><AlertCircle size={14}/> {errors.submit}</p>
                </div>
            )}
          </div>

          {/* Quick Tip */}
          <div className="bg-gray-900 rounded-[2.5rem] p-8 text-white relative overflow-hidden group shadow-2xl">
            <div className="absolute -top-10 -right-10 w-32 h-32 bg-primary-600/20 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700"></div>
            <div className="relative z-10">
                <div className="bg-primary-600 w-10 h-10 rounded-xl flex items-center justify-center mb-4">
                    <Info size={20} />
                </div>
                <h3 className="font-black mb-2">نصيحة للمدرس</h3>
                <p className="text-gray-400 text-xs leading-relaxed font-medium">
                    العناوين الواضحة والصور الجذابة تزيد من نسبة اشتراك الطلاب في الكورس بنسبة 40%. حاول اختيار غلاف يعبر عن محتوى المادة بدقة.
                </p>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreateCourse;